import { Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { DEPLOY_STATUS_PATH, PERMISSIONS } from '@asyagrup/group-hub-contracts';
import { useSession } from './session-context';
import { RequirePermission } from './RequirePermission';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import { RouteFallback } from './RouteFallback';
import { lazyRoute } from './lazy-route';
import { Button } from '@/ui/primitives';
import { LoginPage } from '@/features/auth/LoginPage';
import { AppShell } from '@/layouts/AppShell';
import { ChangePasswordRequired } from '@/features/auth/ChangePasswordRequired';
// Tembel yüklenmiyor, bilerek: bu sayfa oturum çözülmeden ÖNCE çiziliyor ve
// `lazyRoute`'un dayandığı yönlendirici/askı düzeni henüz kurulmuş değil.
// Maliyeti küçük bir bileşen; karşılığı, dışarıdan gelen tek bağlantının
// yüklenme sırasına bağlı olmaması.
import { ReconciliationPublicPage } from '@/features/payee/ReconciliationPublicPage';

// Lazy per feature: the admin screens are a minority of sessions and should not
// sit in the bundle every employee downloads to file a leave request.
//
// `lazyRoute`, never `React.lazy` — it carries the retry a cold load needs and
// is what the boundary below can recognise a network failure through.
// scripts/check-lazy-routes.mjs turns CI red if a route is added without it.
const DashboardPage = lazyRoute(
  () => import('@/features/dashboard/DashboardPage'),
  (m) => m.DashboardPage,
);
const JobTitlesPage = lazyRoute(
  () => import('@/features/job-titles/JobTitlesPage'),
  (m) => m.JobTitlesPage,
);
const OrgPage = lazyRoute(
  () => import('@/features/org/OrgPage'),
  (m) => m.OrgPage,
);
const UsersPage = lazyRoute(
  () => import('@/features/users/UsersPage'),
  (m) => m.UsersPage,
);
const RolesPage = lazyRoute(
  () => import('@/features/roles/RolesPage'),
  (m) => m.RolesPage,
);
const PaymentWeekPage = lazyRoute(
  () => import('@/features/payment/PaymentWeekPage'),
  (m) => m.PaymentWeekPage,
);
const ApprovalChainPage = lazyRoute(
  () => import('@/features/payment/ApprovalChainPage'),
  (m) => m.ApprovalChainPage,
);
const PayeeRegisterPage = lazyRoute(
  () => import('@/features/payee/PayeeRegisterPage'),
  (m) => m.PayeeRegisterPage,
);
const PayeeLedgerPage = lazyRoute(
  () => import('@/features/payee/PayeeLedgerPage'),
  (m) => m.PayeeLedgerPage,
);
const InvoiceApprovalPage = lazyRoute(
  () => import('@/features/invoice/InvoiceApprovalPage'),
  (m) => m.InvoiceApprovalPage,
);
// Masraf talebi ile personel talebi TEK ekranda: ikisi de «bir şey istemek»,
// ve hangi yarısının nerede olduğunu bilmek kullanıcının işi değil.
const TaleplerimPage = lazyRoute(
  () => import('@/features/talep/TaleplerimPage'),
  (m) => m.TaleplerimPage,
);
const BudgetPage = lazyRoute(
  () => import('@/features/finance/BudgetPage'),
  (m) => m.BudgetPage,
);
const FinancePage = lazyRoute(
  () => import('@/features/finance/FinancePage'),
  (m) => m.FinancePage,
);
const TreasuryPage = lazyRoute(
  () => import('@/features/treasury/TreasuryPage'),
  (m) => m.TreasuryPage,
);
const CorrectionPage = lazyRoute(
  () => import('@/features/correction/CorrectionPage'),
  (m) => m.CorrectionPage,
);
const ProjectsPage = lazyRoute(
  () => import('@/features/project/ProjectsPage'),
  (m) => m.ProjectsPage,
);
const CashFlowPage = lazyRoute(
  () => import('@/features/treasury/CashFlowPage'),
  (m) => m.CashFlowPage,
);
const AttendancePage = lazyRoute(
  () => import('@/features/attendance/AttendancePage'),
  (m) => m.AttendancePage,
);
const FlightPremiumPage = lazyRoute(
  () => import('@/features/flight-premium/FlightPremiumPage'),
  (m) => m.FlightPremiumPage,
);
const IntegrationPage = lazyRoute(
  () => import('@/features/integration/IntegrationPage'),
  (m) => m.IntegrationPage,
);
const MyHistoryPage = lazyRoute(
  () => import('@/features/activity/MyHistoryPage'),
  (m) => m.MyHistoryPage,
);
const ActivityPage = lazyRoute(
  () => import('@/features/activity/ActivityPage'),
  (m) => m.ActivityPage,
);
const AttendancePolicyPage = lazyRoute(
  () => import('@/features/attendance/AttendancePolicyPage'),
  (m) => m.AttendancePolicyPage,
);
const MailPreviewPage = lazyRoute(
  () => import('@/features/mail/MailPreviewPage'),
  (m) => m.MailPreviewPage,
);
const AuditPage = lazyRoute(
  () => import('@/features/audit/AuditPage'),
  (m) => m.AuditPage,
);
const SecurityPage = lazyRoute(
  () => import('@/features/security/SecurityPage'),
  (m) => m.SecurityPage,
);
const DeployStatusPage = lazyRoute(
  () => import('@/features/system/DeployStatusPage'),
  (m) => m.DeployStatusPage,
);
const MyLeavePage = lazyRoute(
  () => import('@/features/leave/MyLeavePage'),
  (m) => m.MyLeavePage,
);
const ApprovalsPage = lazyRoute(
  () => import('@/features/leave/ApprovalsPage'),
  (m) => m.ApprovalsPage,
);
const LeaveRecordsPage = lazyRoute(
  () => import('@/features/leave/LeaveRecordsPage'),
  (m) => m.LeaveRecordsPage,
);
const AssetsPage = lazyRoute(
  () => import('@/features/asset/AssetsPage'),
  (m) => m.AssetsPage,
);
const AssetDetailPage = lazyRoute(
  () => import('@/features/asset/AssetDetailPage'),
  (m) => m.AssetDetailPage,
);
const CustodyListPage = lazyRoute(
  () => import('@/features/asset/CustodyListPage'),
  (m) => m.CustodyListPage,
);
const CustodyDetailPage = lazyRoute(
  () => import('@/features/asset/CustodyListPage'),
  (m) => m.CustodyDetailPage,
);
const AssetReportsPage = lazyRoute(
  () => import('@/features/asset/AssetReportsPage'),
  (m) => m.AssetReportsPage,
);
const MyAssetsPage = lazyRoute(
  () => import('@/features/asset/MyAssetsPage'),
  (m) => m.MyAssetsPage,
);
const MyPayslipsPage = lazyRoute(
  () => import('@/features/payslip/MyPayslipsPage'),
  (m) => m.MyPayslipsPage,
);
const PayslipUploadPage = lazyRoute(
  () => import('@/features/payslip/PayslipUploadPage'),
  (m) => m.PayslipUploadPage,
);
const EmployeesPage = lazyRoute(
  () => import('@/features/employees/EmployeesPage'),
  (m) => m.EmployeesPage,
);
const ProfilePage = lazyRoute(
  () => import('@/features/profile/ProfilePage'),
  (m) => m.ProfilePage,
);
const NotificationsPage = lazyRoute(
  () => import('@/features/notifications/NotificationsPage'),
  (m) => m.NotificationsPage,
);
const KonaklamaPage = lazyRoute(
  () => import('@/features/konaklama/KonaklamaPage'),
  (m) => m.KonaklamaPage,
);
const KonaklamaReservationsPage = lazyRoute(
  () => import('@/features/konaklama/ReservationsPage'),
  (m) => m.KonaklamaReservationsPage,
);
const KonaklamaCollectionsPage = lazyRoute(
  () => import('@/features/konaklama/CollectionsPage'),
  (m) => m.KonaklamaCollectionsPage,
);
const OkulTahsilatPage = lazyRoute(
  () => import('@/features/okul-tahsilat/OkulTahsilatPage'),
  (m) => m.OkulTahsilatPage,
);
const KonaklamaOperationsPage = lazyRoute(
  () => import('@/features/konaklama/OperationsPage'),
  (m) => m.KonaklamaOperationsPage,
);
// Three separate screens, not one with tabs: the period calendar is read every
// week by whoever pays the bills, the contracts are edited a few times a year,
// and the loan schedule is its own question. Bundling them would put the
// rarest and heaviest screen in the download of the most frequent one.
const FixedExpensePage = lazyRoute(
  () => import('@/features/fixed-expense/FixedExpensePage'),
  (m) => m.FixedExpensePage,
);
const FixedExpenseCommitmentsPage = lazyRoute(
  () => import('@/features/fixed-expense/CommitmentsPage'),
  (m) => m.CommitmentsPage,
);
const FixedExpenseLoansPage = lazyRoute(
  () => import('@/features/fixed-expense/LoansPage'),
  (m) => m.FixedExpenseLoansPage,
);

// Two screens, not one: the fleet list is opened daily and the contract detail
// carries the hour-entry grid and the payment legs. Bundling them would put the
// heavier screen in the download of the more frequent one.
const LeasedAircraftPage = lazyRoute(
  () => import('@/features/leased-aircraft/LeasedAircraftPage'),
  (m) => m.LeasedAircraftPage,
);
const LeaseContractPage = lazyRoute(
  () => import('@/features/leased-aircraft/LeaseContractPage'),
  (m) => m.LeaseContractPage,
);

const SupportPage = lazyRoute(
  () => import('@/features/support/SupportPage'),
  (m) => m.SupportPage,
);

const DocumentLibraryPage = lazyRoute(
  () => import('@/features/document-library/DocumentLibraryPage'),
  (m) => m.DocumentLibraryPage,
);

const CommunicationPage = lazyRoute(
  () => import('@/features/communication/CommunicationPage'),
  (m) => m.CommunicationPage,
);

const AnnouncementsPage = lazyRoute(
  () => import('@/features/announcements/AnnouncementsPage'),
  (m) => m.AnnouncementsPage,
);

const MessagingPage = lazyRoute(
  () => import('@/features/messaging/MessagingPage'),
  (m) => m.MessagingPage,
);

const AgendaPage = lazyRoute(
  () => import('@/features/agenda/AgendaPage'),
  (m) => m.AgendaPage,
);

const MaintenanceJobsPage = lazyRoute(
  () => import('@/features/maintenance/MaintenanceJobsPage'),
  (m) => m.MaintenanceJobsPage,
);
const TaxCalendarPage = lazyRoute(
  () => import('@/features/tax/TaxCalendarPage'),
  (m) => m.TaxCalendarPage,
);
const TaxpayersPage = lazyRoute(
  () => import('@/features/tax/TaxpayersPage'),
  (m) => m.TaxpayersPage,
);
const TaxTypesPage = lazyRoute(
  () => import('@/features/tax/TaxTypesPage'),
  (m) => m.TaxTypesPage,
);

function FullPageSpinner() {
  return (
    <div className="bg-bg text-fg-muted flex min-h-dvh items-center justify-center">
      <Loader2 className="animate-spin" size={20} aria-label="Yükleniyor" />
    </div>
  );
}

/**
 * Shown when the boot session restore never got an answer — NOT when the
 * answer was "no session".
 *
 * The login form used to stand in for this, and it lied twice: it told people
 * their session had ended when it had not, and it asked for the one thing that
 * could not help. Someone who has just been thrown out retypes their password;
 * a wrong address typed a few times trips the per-IP lockout and takes the
 * office with it. Saying "we could not reach the server" costs nothing and
 * points at the actual problem.
 */
function ConnectionLost() {
  return (
    <div className="bg-bg flex min-h-dvh items-center justify-center px-4">
      <div className="border-border bg-surface w-full max-w-sm rounded-xl border px-4 py-5 text-center">
        <p className="text-fg text-lg leading-[1.5] font-semibold">Sunucuya ulaşılamadı</p>
        <p className="text-fg-muted mt-2 text-sm leading-[1.5]">
          Oturumunuz kapanmadı — bağlantı kurulamadığı için doğrulanamadı. İnternet bağlantınızı
          kontrol edip tekrar deneyin; sistem güncelleniyorsa birkaç saniye içinde düzelir.
        </p>
        <Button className="mt-4 w-full" onClick={() => window.location.reload()}>
          Tekrar dene
        </Button>
      </div>
    </div>
  );
}

/**
 * Oturum kapılarından ÖNCE ele alınan yollar.
 *
 * Mutabakat cevap sayfasını açan kişi bizim kullanıcımız değil: karşı tarafın
 * muhasebecisi, e-postadaki bağlantıya tıklıyor. Aşağıdaki `anonymous` kapısı
 * onu giriş ekranına düşürürdü — yani gönderdiğimiz bağlantı, alıcıyı parola
 * isteyen bir forma götüren bir bağlantı olurdu ve kimse bir daha tıklamazdı.
 *
 * Kimlik yerine adresteki belirteç geçiyor ve sunucu onu doğruluyor; burada
 * yapılan tek şey, oturum yokluğunun bu yolu ele geçirmesini engellemek.
 */
const PUBLIC_PATHS = [/^\/mutabakat\//];

export function App() {
  const { status, user } = useSession();

  if (PUBLIC_PATHS.some((re) => re.test(window.location.pathname))) {
    return <ReconciliationPublicPage />;
  }

  // A skeleton while the session resolves — never a menu. Rendering navigation
  // before permissions are known is how a UI ends up "failing open" visually
  // even when the API does not.
  if (status === 'loading') return <FullPageSpinner />;
  // Before the anonymous gate, because it is the case that gate used to
  // swallow: an unanswered boot request is not a logged-out user.
  if (status === 'unreachable') return <ConnectionLost />;
  if (status === 'anonymous') return <LoginPage />;

  // Before the router, not inside it. The server refuses every other endpoint
  // with PASSWORD_CHANGE_REQUIRED anyway, so rendering the app here would
  // produce a shell of failing requests and no explanation of why.
  if (user?.mustChangePassword) return <ChangePasswordRequired />;

  return (
    <BrowserRouter>
      <AppShell>
        {/* Inside the shell, so a failed screen leaves the navigation standing
            and the way out is a menu item. Outside <Suspense>, so it catches
            the loader's rejection rather than the fallback. */}
        <RouteErrorBoundary>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route
                path="/yonetim/unvanlar"
                element={
                  <RequirePermission permission={PERMISSIONS.ORG_UNIT_READ}>
                    <JobTitlesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/kurulus"
                element={
                  <RequirePermission permission={PERMISSIONS.ORG_UNIT_READ}>
                    <OrgPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/izinlerim"
                element={
                  <RequirePermission permission={PERMISSIONS.LEAVE_REQUEST_CREATE}>
                    <MyLeavePage />
                  </RequirePermission>
                }
              />
              <Route
                path="/onaylarim"
                element={
                  <RequirePermission permission={PERMISSIONS.LEAVE_REQUEST_APPROVE}>
                    <ApprovalsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/izinler"
                element={
                  <RequirePermission permission={PERMISSIONS.LEAVE_REQUEST_READ}>
                    <LeaveRecordsPage />
                  </RequirePermission>
                }
              />
              {/*
                Sabit parçalar `/zimmet/:id` benzeri bir rotadan ÖNCE gelmek
                zorunda değil — burada öyle bir rota yok, varlık detayı
                `/zimmet/varlik/:id` altında. Bilerek: `/zimmet/raporlar` ile
                bir varlık kimliğinin aynı desene düşebildiği bir düzen, ilk
                eklenen rapor sayfasında sessizce 404 üretirdi.
              */}
              <Route
                path="/zimmet"
                element={
                  <RequirePermission permission={PERMISSIONS.ASSET_READ}>
                    <AssetsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/zimmet/varlik/:id"
                element={
                  <RequirePermission permission={PERMISSIONS.ASSET_READ}>
                    <AssetDetailPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/zimmet/tutanaklar"
                element={
                  <RequirePermission permission={PERMISSIONS.ASSET_READ}>
                    <CustodyListPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/zimmet/tutanak/:id"
                element={
                  <RequirePermission permission={PERMISSIONS.ASSET_READ}>
                    <CustodyDetailPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/zimmet/raporlar"
                element={
                  <RequirePermission permission={PERMISSIONS.ASSET_REPORT}>
                    <AssetReportsPage />
                  </RequirePermission>
                }
              />
              {/*
                Kaydın MUHATABI için. `asset.read` değil `asset.read_own` ile
                açılıyor ve o kodu her rol taşıyor — zimmetini göremeyen biri
                teslim aldığını da beyan edemez.
              */}
              <Route
                path="/zimmetlerim"
                element={
                  <RequirePermission permission={PERMISSIONS.ASSET_READ_OWN}>
                    <MyAssetsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/bordrolarim"
                element={
                  <RequirePermission permission={PERMISSIONS.PAYSLIP_READ_OWN}>
                    <MyPayslipsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/bordro"
                element={
                  <RequirePermission permission={PERMISSIONS.PAYSLIP_MANAGE}>
                    <PayslipUploadPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/personel"
                element={
                  <RequirePermission permission={PERMISSIONS.EMPLOYEE_READ}>
                    <EmployeesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/kullanicilar"
                element={
                  <RequirePermission permission={PERMISSIONS.USER_READ}>
                    <UsersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/roller"
                element={
                  <RequirePermission permission={PERMISSIONS.ROLE_READ}>
                    <RolesPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/puantaj-politikasi"
                element={
                  <RequirePermission permission={PERMISSIONS.ATTENDANCE_READ}>
                    <AttendancePolicyPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/aktivite"
                element={
                  <RequirePermission permission={PERMISSIONS.ACTIVITY_READ}>
                    <ActivityPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/eposta-onizleme"
                element={
                  <RequirePermission permission={PERMISSIONS.SETTINGS_MANAGE}>
                    <MailPreviewPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/denetim"
                element={
                  <RequirePermission permission={PERMISSIONS.AUDIT_READ}>
                    <AuditPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/guvenlik"
                element={
                  <RequirePermission permission={PERMISSIONS.SECURITY_READ}>
                    <SecurityPage />
                  </RequirePermission>
                }
              />
              <Route
                path={DEPLOY_STATUS_PATH}
                element={
                  <RequirePermission permission={PERMISSIONS.JOB_READ}>
                    <DeployStatusPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/odeme-tablosu"
                element={
                  <RequirePermission permission={PERMISSIONS.PAYMENT_REQUEST_CREATE}>
                    <PaymentWeekPage />
                  </RequirePermission>
                }
              />
              <Route
                /* Faturanın KENDİ kapısı var, cari ekranınınki değil: cari
                   `payment_request.create` ile açılıyor ve o izin her
                   çalışanda — fatura tutarlarını oraya koymak, bordro dışı
                   bütün gider akışını herkese açardı. */
                path="/fatura-onay"
                element={
                  <RequirePermission permission={PERMISSIONS.INVOICE_READ}>
                    <InvoiceApprovalPage />
                  </RequirePermission>
                }
              />
              <Route
                /* Ekran «Taleplerim»e taşındı; adres yaşamaya devam ediyor.
                   Sunucu bildirimleri hâlâ bu yolu yazıyor ve kullanıcıların
                   yer imleri de burada. Sekme adreste taşındığı için
                   yönlendirme doğru yarıya düşüyor. */
                path="/personel-talepleri"
                element={<Navigate to="/taleplerim?sekme=ihtiyac" replace />}
              />
              <Route
                /* Kapı `payment_request.create`ten `payee.manage`e DARALDI.
                   Eski gerekçe şuydu: defteri okumak talebi açan kişinin işinin
                   parçası, çünkü aynı IBAN'ı bugün elle yazan o. O cümle bir
                   SATIRI seçmek için doğru, DEFTERİN TAMAMINI gezmek için değil
                   — bu ekran kimlerle çalıştığımızın, kaç tedarikçimiz
                   olduğunun ve kimin hangi bankayla çalıştığının listesi, ve o
                   sorular talebi açan kişinin işi değil.
                   Talebi açan kişi carisini artık ödeme formundaki aramadan
                   buluyor: dört harf yazar, aradığını seçer, listeyi görmez. */
                path="/cari"
                element={
                  <RequirePermission permission={PERMISSIONS.PAYEE_MANAGE}>
                    <PayeeRegisterPage />
                  </RequirePermission>
                }
              />
              <Route
                /* Bakiye, kartın kendisinden daha dar bir çevre: kayıt defterini
                   okumak talep açan herkesin işi, "şu firmaya 400.000 TL
                   borcumuz var" değil. payment.read zaten "başkalarının
                   ödemelerini gör" demek ve aynı çevre. */
                path="/cari-hesap"
                element={
                  <RequirePermission permission={PERMISSIONS.PAYMENT_READ}>
                    <PayeeLedgerPage />
                  </RequirePermission>
                }
              />
              <Route
                /* Zinciri kurmak, zincirden geçmekle aynı yetki değil: ödeme
                 tablosunu okuyan herkes buraya giremez, çünkü kapıların sırasını
                 ve kapılardaki isimleri değiştirmek onay vermekten daha ileri
                 bir yetki. */
                path="/odeme-onay-zinciri"
                element={
                  <RequirePermission permission={PERMISSIONS.PAYMENT_APPROVAL_CHAIN_MANAGE}>
                    <ApprovalChainPage />
                  </RequirePermission>
                }
              />
              <Route
                /* Masraf ve ihtiyaç talebi tek ekranda. Kapı iki izinden
                   BİRİ olduğu için `RequirePermission` burada yok — o bileşen
                   tek izin soruyor; «herhangi biri» kararını sayfanın kendisi
                   veriyor ve reddi yine aynı ekranla çiziyor. */
                path="/taleplerim"
                element={<TaleplerimPage />}
              />
              <Route path="/masraflar" element={<Navigate to="/taleplerim" replace />} />
              <Route
                path="/butce"
                element={
                  /* Okumak finance.read; bu ekranın yazma denetimleri kendi
                   yetkilerine (budget.manage / budget.lock) ayrıca bakıyor. */
                  <RequirePermission permission={PERMISSIONS.FINANCE_READ}>
                    <BudgetPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/kasa-banka"
                element={
                  <RequirePermission permission={PERMISSIONS.TREASURY_READ}>
                    <TreasuryPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/yonetim/veri-duzeltme"
                element={
                  <RequirePermission permission={PERMISSIONS.CORRECTION_READ}>
                    <CorrectionPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/projeler"
                element={
                  /* Görmek project.read; ekranın yazma denetimleri ayrıca
                     project.manage'e bakıyor. Doğrudan URL ile gelen yetkisiz
                     kullanıcı burada duruyor, backend'de de aynı kapı var. */
                  <RequirePermission permission={PERMISSIONS.PROJECT_READ}>
                    <ProjectsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/nakit-akis"
                element={
                  <RequirePermission permission={PERMISSIONS.TREASURY_READ}>
                    <CashFlowPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/karlilik"
                element={
                  <RequirePermission permission={PERMISSIONS.FINANCE_READ}>
                    <FinancePage />
                  </RequirePermission>
                }
              />
              <Route
                /* All three open on fixed_expense.read; editing a commitment and
                 approving an escalation are checked again by the screens' own
                 controls. Seeing the due calendar is not being able to grow
                 the amount. */
                path="/sabit-giderler"
                element={
                  <RequirePermission permission={PERMISSIONS.FIXED_EXPENSE_READ}>
                    <FixedExpensePage />
                  </RequirePermission>
                }
              />
              {/*
                Katalog ekranı yazma iznini KENDİ içinde soruyor; rota yalnız
                `tax.read` istiyor. Rotayı `tax.calendar_manage`'e bağlamak,
                mevzuat tarihlerini yalnız okuması gereken herkesi — yani
                mükellefiyet açan finans uzmanını — kapının dışında bırakırdı.
              */}
              <Route
                path="/vergi"
                element={
                  <RequirePermission permission={PERMISSIONS.TAX_READ}>
                    <TaxCalendarPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/vergi/mukellefler"
                element={
                  <RequirePermission permission={PERMISSIONS.TAX_READ}>
                    <TaxpayersPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/vergi/turler"
                element={
                  <RequirePermission permission={PERMISSIONS.TAX_READ}>
                    <TaxTypesPage />
                  </RequirePermission>
                }
              />
              <Route
                /* Both open on leased_aircraft.read. Entering hours, confirming
                 them and filing a payment are checked again by the screens' own
                 controls — reading the guarantee is not deciding what is paid
                 for it. */
                path="/kiralik-ucak"
                element={
                  <RequirePermission permission={PERMISSIONS.LEASED_AIRCRAFT_READ}>
                    <LeasedAircraftPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/kiralik-ucak/sozlesmeler/:id"
                element={
                  <RequirePermission permission={PERMISSIONS.LEASED_AIRCRAFT_READ}>
                    <LeaseContractPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/sabit-giderler/sozlesmeler"
                element={
                  <RequirePermission permission={PERMISSIONS.FIXED_EXPENSE_READ}>
                    <FixedExpenseCommitmentsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/sabit-giderler/krediler"
                element={
                  <RequirePermission permission={PERMISSIONS.FIXED_EXPENSE_READ}>
                    <FixedExpenseLoansPage />
                  </RequirePermission>
                }
              />
              {/* Destek ve Yenilikler tek ekranda; kapı SAYFANIN İÇİNDE. Yenilikler
                herkesin (bkz. aşağıdaki `/yenilikler`), destek kayıtları
                `support.read_own` istiyor — support.read değil: herkes kendi
                bildirdiğini takip edebilmeli. `RequirePermission` tek izin
                soruyor; hangi yarının çizileceğine sayfa karar veriyor ve
                listeyi kimin neye kadar gördüğünü servis belirliyor. */}
              <Route path="/destek" element={<SupportPage />} />
              {/* document_library.read, document.read değil: ikincisi bir
                personelin özlük evrakını okuma yetkisi ve bambaşka bir çevre.
                Ortak bir prosedürü okuyabilsin diye birine bütün personelin
                özlük dosyasını açmak, bu ayrımın olmaması demek olurdu. */}
              <Route
                path="/dokumanlar"
                element={
                  <RequirePermission permission={PERMISSIONS.DOCUMENT_LIBRARY_READ}>
                    <DocumentLibraryPage />
                  </RequirePermission>
                }
              />
              {/* İletişim Merkezinin kapısı. Kapısı /duyurular ile AYNI kod ve
                aynı gerekçeyle: bu ekranın altı bölümünden dördü tebligat
                tarafından geliyor. Tek bir koda bağlanması gerekiyordu — rota
                kapısı istediklerini AND'liyor, yani message.use'u da istemek
                yalnız birini taşıyan bir hesaba ekranı bütünüyle kapatırdı.
                Bölüm bazında kapıyı sunucu tutuyor: yetkisi olmayan bölüm
                cevapta hiç yok, boş gelmiyor. */}
              <Route
                path="/iletisim"
                element={
                  <RequirePermission permission={PERMISSIONS.ANNOUNCEMENT_READ}>
                    <CommunicationPage />
                  </RequirePermission>
                }
              />
              {/* announcement.read, herkeste olan kod: kendi işvereninin
                tebliğini okumak için izin istemek, okunmayan tebliğ üretir.
                Yayım ve makbuz yetkileri ekranın içinde ayrıca soruluyor. */}
              <Route
                path="/duyurular"
                element={
                  <RequirePermission permission={PERMISSIONS.ANNOUNCEMENT_READ}>
                    <AnnouncementsPage />
                  </RequirePermission>
                }
              />
              {/* message.use, herkeste olan kod: bir meslektaşına yazmak için
                izin istemek, sistemin DIŞINDA yazışan bir şirket üretir. Grup
                ve kanal açma kodları ekranın içinde ayrıca soruluyor; hangi
                konuşmayı görebildiğine izin değil ÜYELİK karar veriyor ve API
                her uçta yeniden soruyor. */}
              <Route
                path="/mesajlar"
                element={
                  <RequirePermission permission={PERMISSIONS.MESSAGE_USE}>
                    <MessagingPage />
                  </RequirePermission>
                }
              />
              {/* Sözlük artık Duyurular'ın «Kategoriler» sekmesi; adres yer
                imleri için yaşıyor. Ayrı YETKİ duruyor (kategori eklemek,
                duyuru yayımlamaktan başka bir iştir) ama kapı rotada değil
                sekmede: `announcement.category_manage` taşımayana sekme
                çizilmiyor ve bu adresle gelen gelen kutusunu görüyor. */}
              <Route
                path="/duyurular/kategoriler"
                element={<Navigate to="/duyurular?sekme=categories" replace />}
              />
              {/* agenda.use, agenda.assign değil: kendi gününü yazmak herkesin
                işi. Görevlendirme kutusunun görünüp görünmeyeceğine ekran
                kendi bakıyor, ve API her hâlükârda yeniden soruyor. */}
              <Route
                path="/ajanda"
                element={
                  <RequirePermission permission={PERMISSIONS.AGENDA_USE}>
                    <AgendaPage />
                  </RequirePermission>
                }
              />
              {/* maintenance.read, finance.read değil: bu ekran bir mali tablo
                değil bir takip listesi, ve faturayı kovalayan kişiye grubun
                bütün finans verisini açmak gerekmiyor. */}
              <Route
                path="/bakim-isleri"
                element={
                  <RequirePermission permission={PERMISSIONS.MAINTENANCE_READ}>
                    <MaintenanceJobsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/puantaj"
                element={
                  <RequirePermission permission={PERMISSIONS.ATTENDANCE_READ}>
                    <AttendancePage />
                  </RequirePermission>
                }
              />
              <Route
                /* Kapı okuma izni; ekranın kendi yazma denetimleri ayrıca
                   `flight_premium.record`, `.rate_manage` ve `.file_payment`e
                   bakıyor. Saatlik oran bir maaş bilgisidir ve bu yüzden
                   `employee.read`ten AYRI bir izinle korunuyor — personel
                   kartını görebilen herkes kimin saati kaça uçtuğunu görmez. */
                path="/ucus-primleri"
                element={
                  <RequirePermission permission={PERMISSIONS.FLIGHT_PREMIUM_READ}>
                    <FlightPremiumPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/entegrasyon"
                element={
                  <RequirePermission permission={PERMISSIONS.INTEGRATION_READ}>
                    <IntegrationPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/misafirhane"
                element={
                  <RequirePermission permission={PERMISSIONS.KONAKLAMA_READ}>
                    <KonaklamaPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/misafirhane/rezervasyon"
                element={
                  <RequirePermission permission={PERMISSIONS.KONAKLAMA_READ}>
                    <KonaklamaReservationsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/misafirhane/tahsilat"
                element={
                  <RequirePermission permission={PERMISSIONS.KONAKLAMA_READ}>
                    <KonaklamaCollectionsPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/okul-tahsilat"
                element={
                  <RequirePermission permission={PERMISSIONS.OKUL_TAHSILAT_READ}>
                    <OkulTahsilatPage />
                  </RequirePermission>
                }
              />
              <Route
                path="/misafirhane/isletme"
                element={
                  <RequirePermission permission={PERMISSIONS.KONAKLAMA_READ}>
                    <KonaklamaOperationsPage />
                  </RequirePermission>
                }
              />
              <Route path="/profil" element={<ProfilePage />} />
              {/* No permission gate: a notification inbox holds only rows that
                were addressed to this user. It is also where a push
                notification lands when its actionUrl is gone. */}
              <Route path="/bildirimler" element={<NotificationsPage />} />
              {/* Yenilikler, Destek ekranının bir sekmesi oldu; adres YAŞIYOR ve
                silinmemeli. Sunucunun sürüm bildirimleri (release-announcer)
                bu yolu yazıyor — kayıtlı satırlar ve yer imleri de burada —
                ve silinseydi aşağıdaki `*` okuyucuyu sessizce panoya
                bırakırdı. Kapı yine yok: ürünün neyin değiştiği kimsenin
                özel işi değil. */}
              <Route
                path="/yenilikler"
                element={<Navigate to="/destek?sekme=yenilikler" replace />}
              />
              {/* Kendi izini görmek bir ayrıcalık değil; kapı `dashboard.self`,
                  yani "bana dair" yüzeyinin kapısı. Yeni bir izin kodu
                  açılmadı — bir kişisel görünüm için bütün oturumların API'sini
                  durdurmaya değmez. */}
              <Route
                path="/islemlerim"
                element={
                  <RequirePermission permission={PERMISSIONS.DASHBOARD_SELF}>
                    <MyHistoryPage />
                  </RequirePermission>
                }
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </RouteErrorBoundary>
      </AppShell>
    </BrowserRouter>
  );
}
